<template>
  <div class="form-check mb-2">
    <input
      v-bind="$attrs"
      type="checkbox"
      class="form-check-input"
      :checked="checked"
      :class="{
        'is-valid': validFeedback,
        'is-invalid': invalidFeedback ?? (meta.validated && !meta.valid),
      }"
      :id="id"
      :value="true"
      :required="required"
      @change="handleChange"
    />

    <FieldLabel class="form-check-label" :for="id" :required="required">
      <slot>{{ label }}</slot>
    </FieldLabel>

    <FieldMessage
      :help-text="helpText"
      :invalid-feedback="invalidFeedback ?? errorMessage"
      :valid-feedback="validFeedback"
    />
  </div>
</template>

<script lang="ts">
export type CheckboxFieldProps = FieldMessageProps & {
  checked?: boolean;
  helpText?: string;
  id?: string;
  label?: string | boolean;
  name?: string;
  required?: boolean;
  rules?: BooleanSchema;
};

export type CheckboxFieldEvents = {
  (event: "update:checked", value: boolean): unknown;
};
</script>

<script setup lang="ts">
import { uniqueId } from "underscore";
import { useField } from "vee-validate";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { BooleanSchema, boolean } from "yup";
import FieldLabel from "./FieldLabel.vue";
import FieldMessage, { FieldMessageProps } from "./FieldMessage.vue";

const { t } = useI18n();

const props = defineProps<CheckboxFieldProps>();
const emit = defineEmits<CheckboxFieldEvents>();

const id = computed(() => props.id ?? uniqueId("cb-"));

const rules = computed(() => {
  let schema = boolean();

  if (props.required) {
    schema = schema
      .oneOf([true], t("form.field.checkbox.invalid.required"))
      .required(t("form.field.checkbox.invalid.required"));
  }
  if (props.rules) {
    schema = schema.concat(props.rules);
  }

  return schema;
});

const { checked, errorMessage, meta, handleChange } = useField<boolean>(
  () => props.name ?? id.value,
  rules,
  {
    type: "checkbox",
    checkedValue: true,
    initialValue: props.checked,
  }
);

// Emit `update:checked` when `field.checked` changes.
watch(
  () => checked?.value,
  (value) => emit("update:checked", value ?? false),
  { immediate: true }
);

// Bind `props.checked` to `field.checked`.
watch(
  () => props.checked,
  () => {
    if (checked && props.checked !== checked.value) {
      handleChange(null);
    }
  }
);
</script>
